import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Statistic } from 'antd';
import type { Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { fetchSqlData } from '../../../services/api';
import type {
  AdmFuelPump,
  AdmFuelSourceTransfer,
  AdmVehicle,
  FuelType,
  VwAdmVehicleFuelEntryEnriched,
} from '../../../types';
import FilterBar from '../../../components/common/FilterBar';
import StandardTable from '../../../components/common/StandardTable';
import { useTranslation } from 'react-i18next';

type StockRow = {
  key: string;
  asset_type: 'pump' | 'tanker';
  asset_name: string;
  fuel_type: FuelType | string;
  status?: string | null;
  location?: string | null;
  opening_stock: number;
  incoming_range: number;
  outgoing_range: number;
  dispensed_range: number;
  closing_stock: number;
};

type VehicleConsumptionRow = {
  key: number;
  vehicle_id: number;
  vehicle_name: string;
  fuel_type?: FuelType | null;
  total_liters: number;
  entry_count: number;
  avg_liters: number;
  km_delta?: number | null;
  liters_per_100km?: number | null;
  hours_delta?: number | null;
  liters_per_hour?: number | null;
  last_entry_date?: string | null;
};

type LedgerRow = {
  key: string;
  movement_date: string;
  movement_type: 'transfer_in' | 'transfer_out' | 'dispense';
  source_label: string;
  target_label: string;
  reference_no?: string | null;
  location?: string | null;
  note?: string | null;
  in_qty: number;
  out_qty: number;
  balance: number;
};

const FuelOverview: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-US';
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [selectedFuelType, setSelectedFuelType] = useState<FuelType | undefined>(undefined);
  const [selectedAssetKey, setSelectedAssetKey] = useState<string | undefined>(undefined);

  const { data: fuelEntries = [], isLoading: fuelEntriesLoading } = useQuery<VwAdmVehicleFuelEntryEnriched[], Error>({
    queryKey: ['machineryFuelEntries'],
    queryFn: () => fetchSqlData('vw_adm_vehicle_fuel_entries_enriched'),
    staleTime: 60_000,
  });
  const { data: transfers = [], isLoading: transfersLoading } = useQuery<AdmFuelSourceTransfer[], Error>({
    queryKey: ['fuelSourceTransfers'],
    queryFn: () => fetchSqlData('adm_fuel_source_transfers'),
    staleTime: 60_000,
  });
  const { data: fuelPumps = [], isLoading: pumpsLoading } = useQuery<AdmFuelPump[], Error>({
    queryKey: ['fuelPumps'],
    queryFn: () => fetchSqlData('adm_fuel_pumps'),
    staleTime: 5 * 60_000,
  });
  const { data: vehicles = [], isLoading: vehiclesLoading } = useQuery<AdmVehicle[], Error>({
    queryKey: ['machineryVehicles'],
    queryFn: () => fetchSqlData('adm_vehicles'),
    staleTime: 5 * 60_000,
  });

  const isLoading = fuelEntriesLoading || transfersLoading || pumpsLoading || vehiclesLoading;

  const startDate = dateRange?.[0]?.startOf('day') ?? null;
  const endDate = dateRange?.[1]?.endOf('day') ?? null;

  const fuelTypeOptions = useMemo(
    () => ['Diesel', 'Gasoline', 'LPG', 'Electric', 'Hybrid'].map(value => ({ value, label: value })),
    []
  );

  const tankerVehicles = useMemo(
    () => vehicles.filter(vehicle => vehicle.machine_type === 'Fuel Tanker'),
    [vehicles]
  );

  const vehicleMap = useMemo(() => {
    const map: Record<number, string> = {};
    vehicles.forEach(vehicle => {
      map[vehicle.id] = `${vehicle.license_plate || '-'} ${vehicle.make || ''} ${vehicle.model || ''}`.trim();
    });
    return map;
  }, [vehicles]);

  const isWithinRange = (value?: string | null) => {
    if (!value) return false;
    const current = new Date(value).getTime();
    if (Number.isNaN(current)) return false;
    if (startDate && current < startDate.valueOf()) return false;
    if (endDate && current > endDate.valueOf()) return false;
    return true;
  };

  const isUpToEndDate = (value?: string | null) => {
    if (!value) return false;
    const current = new Date(value).getTime();
    if (Number.isNaN(current)) return false;
    if (endDate && current > endDate.valueOf()) return false;
    return true;
  };

  const filteredTransfers = useMemo(
    () => transfers.filter(row => !selectedFuelType || row.fuel_type === selectedFuelType),
    [selectedFuelType, transfers]
  );

  const filteredEntries = useMemo(
    () => fuelEntries.filter(row => !selectedFuelType || row.fuel_type === selectedFuelType),
    [fuelEntries, selectedFuelType]
  );

  const stockRows = useMemo<StockRow[]>(() => {
    const rows: StockRow[] = [];

    fuelPumps
      .filter(pump => !selectedFuelType || pump.fuel_type === selectedFuelType)
      .forEach(pump => {
        const incomingRange = filteredTransfers
          .filter(row => row.target_type === 'fixed_pump' && Number(row.target_pump_id) === Number(pump.id) && isWithinRange(row.transfer_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        const outgoingTransfersRange = filteredTransfers
          .filter(row => row.source_type === 'fixed_pump' && Number(row.source_pump_id) === Number(pump.id) && isWithinRange(row.transfer_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        const dispensedRange = filteredEntries
          .filter(row => row.source_type === 'fixed_pump' && Number(row.source_pump_id) === Number(pump.id) && isWithinRange(row.entry_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        const incomingCumulative = filteredTransfers
          .filter(row => row.target_type === 'fixed_pump' && Number(row.target_pump_id) === Number(pump.id) && isUpToEndDate(row.transfer_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        const outgoingTransfersCumulative = filteredTransfers
          .filter(row => row.source_type === 'fixed_pump' && Number(row.source_pump_id) === Number(pump.id) && isUpToEndDate(row.transfer_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        const dispensedCumulative = filteredEntries
          .filter(row => row.source_type === 'fixed_pump' && Number(row.source_pump_id) === Number(pump.id) && isUpToEndDate(row.entry_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        rows.push({
          key: `pump-${pump.id}`,
          asset_type: 'pump',
          asset_name: pump.name,
          fuel_type: pump.fuel_type,
          status: pump.status,
          location: pump.location,
          opening_stock: Number(pump.opening_stock_liters || 0),
          incoming_range: incomingRange,
          outgoing_range: outgoingTransfersRange,
          dispensed_range: dispensedRange,
          closing_stock:
            Number(pump.opening_stock_liters || 0) +
            incomingCumulative -
            outgoingTransfersCumulative -
            dispensedCumulative,
        });
      });

    tankerVehicles
      .filter(vehicle => !selectedFuelType || vehicle.fuel_type === selectedFuelType)
      .forEach(vehicle => {
        const incomingRange = filteredTransfers
          .filter(row => row.target_type === 'fuel_tanker' && Number(row.target_vehicle_id) === Number(vehicle.id) && isWithinRange(row.transfer_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        const outgoingTransfersRange = filteredTransfers
          .filter(row => row.source_type === 'fuel_tanker' && Number(row.source_vehicle_id) === Number(vehicle.id) && isWithinRange(row.transfer_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        const dispensedRange = filteredEntries
          .filter(row => row.source_type === 'fuel_tanker' && Number(row.source_vehicle_id) === Number(vehicle.id) && isWithinRange(row.entry_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        const incomingCumulative = filteredTransfers
          .filter(row => row.target_type === 'fuel_tanker' && Number(row.target_vehicle_id) === Number(vehicle.id) && isUpToEndDate(row.transfer_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        const outgoingTransfersCumulative = filteredTransfers
          .filter(row => row.source_type === 'fuel_tanker' && Number(row.source_vehicle_id) === Number(vehicle.id) && isUpToEndDate(row.transfer_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        const dispensedCumulative = filteredEntries
          .filter(row => row.source_type === 'fuel_tanker' && Number(row.source_vehicle_id) === Number(vehicle.id) && isUpToEndDate(row.entry_date))
          .reduce((sum, row) => sum + Number(row.liters || 0), 0);

        rows.push({
          key: `tanker-${vehicle.id}`,
          asset_type: 'tanker',
          asset_name: vehicleMap[vehicle.id] || String(vehicle.id),
          fuel_type: vehicle.fuel_type || '-',
          status: vehicle.status,
          location: null,
          opening_stock: 0,
          incoming_range: incomingRange,
          outgoing_range: outgoingTransfersRange,
          dispensed_range: dispensedRange,
          closing_stock: incomingCumulative - outgoingTransfersCumulative - dispensedCumulative,
        });
      });

    return rows;
  }, [filteredEntries, filteredTransfers, fuelPumps, isUpToEndDate, isWithinRange, selectedFuelType, tankerVehicles, vehicleMap]);

  useEffect(() => {
    if (!stockRows.length) {
      setSelectedAssetKey(undefined);
      return;
    }

    if (!selectedAssetKey || !stockRows.some(row => row.key === selectedAssetKey)) {
      setSelectedAssetKey(stockRows[0].key);
    }
  }, [selectedAssetKey, stockRows]);

  const vehicleConsumptionRows = useMemo<VehicleConsumptionRow[]>(() => {
    const groups = new Map<number, VwAdmVehicleFuelEntryEnriched[]>();

    filteredEntries
      .filter(row => isWithinRange(row.entry_date))
      .forEach(row => {
        const arr = groups.get(Number(row.vehicle_id)) || [];
        arr.push(row);
        groups.set(Number(row.vehicle_id), arr);
      });

    return Array.from(groups.entries()).map(([vehicleId, entries]) => {
      const sortedByDate = [...entries].sort((a, b) => new Date(a.entry_date).getTime() - new Date(b.entry_date).getTime());
      const totalLiters = entries.reduce((sum, row) => sum + Number(row.liters || 0), 0);
      const odometerValues = sortedByDate.map(row => Number(row.odometer_reading)).filter(value => Number.isFinite(value));
      const hoursValues = sortedByDate.map(row => Number(row.hours_counter_reading)).filter(value => Number.isFinite(value));
      const kmDelta = odometerValues.length >= 2 ? Math.max(...odometerValues) - Math.min(...odometerValues) : null;
      const hoursDelta = hoursValues.length >= 2 ? Math.max(...hoursValues) - Math.min(...hoursValues) : null;

      return {
        key: vehicleId,
        vehicle_id: vehicleId,
        vehicle_name: vehicleMap[vehicleId] || String(vehicleId),
        fuel_type: entries[0]?.fuel_type || null,
        total_liters: totalLiters,
        entry_count: entries.length,
        avg_liters: entries.length ? totalLiters / entries.length : 0,
        km_delta: kmDelta && kmDelta > 0 ? kmDelta : null,
        liters_per_100km: kmDelta && kmDelta > 0 ? (totalLiters / kmDelta) * 100 : null,
        hours_delta: hoursDelta && hoursDelta > 0 ? hoursDelta : null,
        liters_per_hour: hoursDelta && hoursDelta > 0 ? totalLiters / hoursDelta : null,
        last_entry_date: sortedByDate[sortedByDate.length - 1]?.entry_date || null,
      };
    }).sort((a, b) => b.total_liters - a.total_liters);
  }, [filteredEntries, isWithinRange, vehicleMap]);

  const totalDispensedRange = useMemo(
    () => filteredEntries.filter(row => isWithinRange(row.entry_date)).reduce((sum, row) => sum + Number(row.liters || 0), 0),
    [filteredEntries]
  );

  const totalTransferredRange = useMemo(
    () => filteredTransfers.filter(row => isWithinRange(row.transfer_date)).reduce((sum, row) => sum + Number(row.liters || 0), 0),
    [filteredTransfers]
  );

  const totalClosingStock = useMemo(
    () => stockRows.reduce((sum, row) => sum + Number(row.closing_stock || 0), 0),
    [stockRows]
  );

  const assetOptions = useMemo(
    () =>
      stockRows.map(row => ({
        value: row.key,
        label: `${row.asset_type === 'pump' ? t('machinery.fuel.overview.assetTypePump') : t('machinery.fuel.overview.assetTypeTanker')} - ${row.asset_name}`,
      })),
    [stockRows, t]
  );

  const selectedAsset = useMemo(
    () => stockRows.find(row => row.key === selectedAssetKey) || null,
    [selectedAssetKey, stockRows]
  );

  const getStatusLabel = (value?: string | null) => {
    if (!value) return '-';
    if (value === 'active') return t('machinery.fuel.pumps.statusActive');
    if (value === 'inactive') return t('machinery.fuel.pumps.statusInactive');
    if (value === 'out_of_service') return t('machinery.fuel.pumps.statusOutOfService');
    if (value === 'Available') return t('machinery.vehicles.common.available');
    if (value === 'Assigned') return t('machinery.vehicles.common.assigned');
    if (value === 'InMaintenance') return t('machinery.vehicles.common.inMaintenance');
    if (value === 'OutOfService') return t('machinery.vehicles.common.outOfService');
    if (value === 'Inactive') return t('machinery.vehicles.common.inactive');
    return value;
  };

  const getAssetLabelFromKey = (assetKey?: string | null) => {
    if (!assetKey) return '-';
    const row = stockRows.find(item => item.key === assetKey);
    return row?.asset_name || assetKey;
  };

  const ledgerRows = useMemo<LedgerRow[]>(() => {
    if (!selectedAsset) return [];

    const [assetType, rawId] = selectedAsset.key.split('-');
    const assetId = Number(rawId);
    const isPump = assetType === 'pump';

    const openingIncomingTransfers = filteredTransfers
      .filter(row =>
        (isPump
          ? row.target_type === 'fixed_pump' && Number(row.target_pump_id) === assetId
          : row.target_type === 'fuel_tanker' && Number(row.target_vehicle_id) === assetId) &&
        (startDate ? new Date(row.transfer_date).getTime() < startDate.valueOf() : true)
      )
      .reduce((sum, row) => sum + Number(row.liters || 0), 0);

    const openingOutgoingTransfers = filteredTransfers
      .filter(row =>
        (isPump
          ? row.source_type === 'fixed_pump' && Number(row.source_pump_id) === assetId
          : row.source_type === 'fuel_tanker' && Number(row.source_vehicle_id) === assetId) &&
        (startDate ? new Date(row.transfer_date).getTime() < startDate.valueOf() : true)
      )
      .reduce((sum, row) => sum + Number(row.liters || 0), 0);

    const openingDispensed = filteredEntries
      .filter(row =>
        (isPump
          ? row.source_type === 'fixed_pump' && Number(row.source_pump_id) === assetId
          : row.source_type === 'fuel_tanker' && Number(row.source_vehicle_id) === assetId) &&
        (startDate ? new Date(row.entry_date).getTime() < startDate.valueOf() : true)
      )
      .reduce((sum, row) => sum + Number(row.liters || 0), 0);

    const openingBalance =
      Number(selectedAsset.opening_stock || 0) +
      openingIncomingTransfers -
      openingOutgoingTransfers -
      openingDispensed;

    const transferInRows: LedgerRow[] = filteredTransfers
      .filter(row =>
        (isPump
          ? row.target_type === 'fixed_pump' && Number(row.target_pump_id) === assetId
          : row.target_type === 'fuel_tanker' && Number(row.target_vehicle_id) === assetId) &&
        isWithinRange(row.transfer_date)
      )
      .map(row => ({
        key: `in-${row.id}`,
        movement_date: row.transfer_date,
        movement_type: 'transfer_in',
        source_label:
          row.source_type === 'fixed_pump'
            ? getAssetLabelFromKey(`pump-${row.source_pump_id}`)
            : row.source_type === 'fuel_tanker'
              ? getAssetLabelFromKey(`tanker-${row.source_vehicle_id}`)
              : row.source_name || '-',
        target_label: selectedAsset.asset_name,
        reference_no: row.reference_no,
        location: row.location,
        note: row.note,
        in_qty: Number(row.liters || 0),
        out_qty: 0,
        balance: 0,
      }));

    const transferOutRows: LedgerRow[] = filteredTransfers
      .filter(row =>
        (isPump
          ? row.source_type === 'fixed_pump' && Number(row.source_pump_id) === assetId
          : row.source_type === 'fuel_tanker' && Number(row.source_vehicle_id) === assetId) &&
        isWithinRange(row.transfer_date)
      )
      .map(row => ({
        key: `out-${row.id}`,
        movement_date: row.transfer_date,
        movement_type: 'transfer_out',
        source_label: selectedAsset.asset_name,
        target_label:
          row.target_type === 'fixed_pump'
            ? getAssetLabelFromKey(`pump-${row.target_pump_id}`)
            : getAssetLabelFromKey(`tanker-${row.target_vehicle_id}`),
        reference_no: row.reference_no,
        location: row.location,
        note: row.note,
        in_qty: 0,
        out_qty: Number(row.liters || 0),
        balance: 0,
      }));

    const dispenseRows: LedgerRow[] = filteredEntries
      .filter(row =>
        (isPump
          ? row.source_type === 'fixed_pump' && Number(row.source_pump_id) === assetId
          : row.source_type === 'fuel_tanker' && Number(row.source_vehicle_id) === assetId) &&
        isWithinRange(row.entry_date)
      )
      .map(row => ({
        key: `dispense-${row.id}`,
        movement_date: row.entry_date,
        movement_type: 'dispense',
        source_label: selectedAsset.asset_name,
        target_label: vehicleMap[Number(row.vehicle_id)] || String(row.vehicle_id),
        reference_no: row.reference_no,
        location: row.location,
        note: row.note,
        in_qty: 0,
        out_qty: Number(row.liters || 0),
        balance: 0,
      }));

    const merged = [...transferInRows, ...transferOutRows, ...dispenseRows].sort((a, b) => {
      const byDate = new Date(a.movement_date).getTime() - new Date(b.movement_date).getTime();
      return byDate !== 0 ? byDate : a.key.localeCompare(b.key);
    });

    let runningBalance = openingBalance;
    return merged.map(row => {
      runningBalance += row.in_qty - row.out_qty;
      return {
        ...row,
        balance: runningBalance,
      };
    });
  }, [filteredEntries, filteredTransfers, isWithinRange, selectedAsset, selectedAssetKey, startDate, stockRows, vehicleMap]);

  const stockColumns = [
    {
      title: t('machinery.fuel.overview.assetType'),
      dataIndex: 'asset_type',
      key: 'asset_type',
      width: 110,
      render: (value: string) => value === 'pump' ? t('machinery.fuel.overview.assetTypePump') : t('machinery.fuel.overview.assetTypeTanker'),
    },
    {
      title: t('machinery.fuel.overview.assetName'),
      dataIndex: 'asset_name',
      key: 'asset_name',
      width: 220,
    },
    {
      title: t('machinery.fuel.overview.fuelType'),
      dataIndex: 'fuel_type',
      key: 'fuel_type',
      width: 100,
    },
    {
      title: t('machinery.fuel.overview.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value: string | null | undefined) => getStatusLabel(value),
    },
    {
      title: t('machinery.fuel.overview.location'),
      dataIndex: 'location',
      key: 'location',
      width: 140,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: t('machinery.fuel.overview.openingStock'),
      dataIndex: 'opening_stock',
      key: 'opening_stock',
      width: 120,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 }),
    },
    {
      title: t('machinery.fuel.overview.incomingRange'),
      dataIndex: 'incoming_range',
      key: 'incoming_range',
      width: 120,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 }),
    },
    {
      title: t('machinery.fuel.overview.outgoingRange'),
      dataIndex: 'outgoing_range',
      key: 'outgoing_range',
      width: 120,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 }),
    },
    {
      title: t('machinery.fuel.overview.dispensedRange'),
      dataIndex: 'dispensed_range',
      key: 'dispensed_range',
      width: 130,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 }),
    },
    {
      title: t('machinery.fuel.overview.closingStock'),
      dataIndex: 'closing_stock',
      key: 'closing_stock',
      width: 130,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 }),
    },
  ];

  const vehicleColumns = [
    {
      title: t('machinery.fuel.overview.vehicle'),
      dataIndex: 'vehicle_name',
      key: 'vehicle_name',
      width: 220,
    },
    {
      title: t('machinery.fuel.overview.fuelType'),
      dataIndex: 'fuel_type',
      key: 'fuel_type',
      width: 100,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: t('machinery.fuel.overview.totalLiters'),
      dataIndex: 'total_liters',
      key: 'total_liters',
      width: 120,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 }),
    },
    {
      title: t('machinery.fuel.overview.entryCount'),
      dataIndex: 'entry_count',
      key: 'entry_count',
      width: 100,
      align: 'right' as const,
    },
    {
      title: t('machinery.fuel.overview.avgLiters'),
      dataIndex: 'avg_liters',
      key: 'avg_liters',
      width: 110,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 }),
    },
    {
      title: t('machinery.fuel.overview.kmDelta'),
      dataIndex: 'km_delta',
      key: 'km_delta',
      width: 100,
      align: 'right' as const,
      render: (value: number | null | undefined) => value?.toLocaleString(locale) || '-',
    },
    {
      title: t('machinery.fuel.overview.litersPer100Km'),
      dataIndex: 'liters_per_100km',
      key: 'liters_per_100km',
      width: 120,
      align: 'right' as const,
      render: (value: number | null | undefined) => value?.toLocaleString(locale, { maximumFractionDigits: 2 }) || '-',
    },
    {
      title: t('machinery.fuel.overview.hoursDelta'),
      dataIndex: 'hours_delta',
      key: 'hours_delta',
      width: 100,
      align: 'right' as const,
      render: (value: number | null | undefined) => value?.toLocaleString(locale, { maximumFractionDigits: 2 }) || '-',
    },
    {
      title: t('machinery.fuel.overview.litersPerHour'),
      dataIndex: 'liters_per_hour',
      key: 'liters_per_hour',
      width: 120,
      align: 'right' as const,
      render: (value: number | null | undefined) => value?.toLocaleString(locale, { maximumFractionDigits: 2 }) || '-',
    },
    {
      title: t('machinery.fuel.overview.lastEntryDate'),
      dataIndex: 'last_entry_date',
      key: 'last_entry_date',
      width: 120,
      render: (value: string | null | undefined) => (value ? new Date(value).toLocaleDateString(locale) : '-'),
    },
  ];

  const ledgerColumns = [
    {
      title: t('machinery.fuel.overview.ledgerDate'),
      dataIndex: 'movement_date',
      key: 'movement_date',
      width: 120,
      render: (value: string) => (value ? new Date(value).toLocaleDateString(locale) : '-'),
    },
    {
      title: t('machinery.fuel.overview.ledgerMovementType'),
      dataIndex: 'movement_type',
      key: 'movement_type',
      width: 140,
      render: (value: string) => {
        if (value === 'transfer_in') return t('machinery.fuel.overview.ledgerTransferIn');
        if (value === 'transfer_out') return t('machinery.fuel.overview.ledgerTransferOut');
        if (value === 'dispense') return t('machinery.fuel.overview.ledgerDispense');
        return value || '-';
      },
    },
    {
      title: t('machinery.fuel.overview.ledgerSource'),
      dataIndex: 'source_label',
      key: 'source_label',
      width: 180,
    },
    {
      title: t('machinery.fuel.overview.ledgerTarget'),
      dataIndex: 'target_label',
      key: 'target_label',
      width: 220,
    },
    {
      title: t('machinery.fuel.overview.referenceNo'),
      dataIndex: 'reference_no',
      key: 'reference_no',
      width: 140,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: t('machinery.fuel.overview.location'),
      dataIndex: 'location',
      key: 'location',
      width: 140,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: t('machinery.fuel.overview.ledgerIn'),
      dataIndex: 'in_qty',
      key: 'in_qty',
      width: 110,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 }),
    },
    {
      title: t('machinery.fuel.overview.ledgerOut'),
      dataIndex: 'out_qty',
      key: 'out_qty',
      width: 110,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 }),
    },
    {
      title: t('machinery.fuel.overview.ledgerBalance'),
      dataIndex: 'balance',
      key: 'balance',
      width: 120,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString(locale, { maximumFractionDigits: 2 }),
    },
    {
      title: t('machinery.fuel.overview.note'),
      dataIndex: 'note',
      key: 'note',
      render: (value: string | null | undefined) => value || '-',
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card title={t('machinery.fuel.overview.title')}>
        <FilterBar
          items={[
            {
              type: 'daterange',
              key: 'dateRange',
              value: dateRange,
              onChange: value => setDateRange(value),
              width: 280,
            },
            {
              type: 'select',
              key: 'fuelType',
              placeholder: t('machinery.fuel.overview.fuelTypePlaceholder'),
              value: selectedFuelType as any,
              onChange: value => setSelectedFuelType(value),
              options: fuelTypeOptions,
              width: 180,
              allowClear: true,
            },
          ]}
          onReset={() => {
            setDateRange(null);
            setSelectedFuelType(undefined);
          }}
        />

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card size="small">
              <Statistic title={t('machinery.fuel.overview.totalDispensedRange')} value={totalDispensedRange} precision={2} />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card size="small">
              <Statistic title={t('machinery.fuel.overview.totalTransferredRange')} value={totalTransferredRange} precision={2} />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card size="small">
              <Statistic title={t('machinery.fuel.overview.totalClosingStock')} value={totalClosingStock} precision={2} />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card size="small">
              <Statistic title={t('machinery.fuel.overview.totalAssetsTracked')} value={stockRows.length} />
            </Card>
          </Col>
        </Row>
      </Card>

      <Card title={t('machinery.fuel.overview.stockTitle')}>
        <StandardTable
          rowKey="key"
          columns={stockColumns as any}
          dataSource={stockRows}
          loading={isLoading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
          bordered
          size="small"
          enableCellTooltips
        />
      </Card>

      <Card title={t('machinery.fuel.overview.vehicleConsumptionTitle')}>
        <StandardTable
          rowKey="key"
          columns={vehicleColumns as any}
          dataSource={vehicleConsumptionRows}
          loading={isLoading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
          bordered
          size="small"
          enableCellTooltips
        />
      </Card>

      <Card title={t('machinery.fuel.overview.ledgerTitle')}>
        <FilterBar
          items={[
            {
              type: 'select',
              key: 'asset',
              placeholder: t('machinery.fuel.overview.assetPlaceholder'),
              value: selectedAssetKey as any,
              onChange: value => setSelectedAssetKey(value),
              options: assetOptions,
              width: 320,
              allowClear: false,
              showSearch: true,
            },
          ]}
        />

        <StandardTable
          rowKey="key"
          columns={ledgerColumns as any}
          dataSource={ledgerRows}
          loading={isLoading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
          bordered
          size="small"
          enableCellTooltips
        />
      </Card>
    </div>
  );
};

export default FuelOverview;
